import { useEffect } from "react";
import useValidation from "../../../../hooks/useValidation";
import { fetchCompanyDetailsByRcs } from "../../../../utils/functions/fetchCompanyDetailsByRcs";
import { getCityWithPreposition } from "../../../../utils/functions/getCityWithPreposition";
import { isValidRcs } from "../../../../utils/functions/validateRCSNumber";
import legalEntityValidationSchema from "../../../../utils/validation/legalEntityValidationSchema";
import CapitalInput from "../../../commonComponents/CapitalInput";
import PostalCodeField from "../../../commonComponents/PostalCodeField";
import RcsInput from "../../../commonComponents/RcsInput";
import FieldDescription from "../../../tools/FieldDescription";
import { LegalEntityWithRepresentativeType } from "../../../../static/types/commonTypes/legalEntityType";
import { civilities } from "../../../../static/types/commonTypes/individualType";
import legalEntityWithRepresentativeValidationSchema from "../../../../utils/validation/limited-company-creation/legalEntityWithRepresentativeValidationSchema";

type legalEntityProps = {
    checked: boolean,
    legalEntity: LegalEntityWithRepresentativeType,
    representativeLabel:string,
    societyType?: string | string [],
    changeLegalEntity: ( data: Partial<LegalEntityWithRepresentativeType> ) => void,
    onValidationChange: ( isValid: boolean ) => void
}

export default function LegalEntityWithRepresentative ({
    checked,
    legalEntity,
    representativeLabel,
    societyType,
    changeLegalEntity,
    onValidationChange
}: legalEntityProps ) {

    
    const legalFormOptions = Array.isArray(societyType) ? societyType : [societyType];

    const { errors, isValid } = useValidation( legalEntity, legalEntityWithRepresentativeValidationSchema );

    useEffect( () => {
        onValidationChange( isValid )
    }, [ isValid, onValidationChange ])

    const handleRcsChange = async (rcsValue: string) => {
  const cleanRcs = rcsValue.replace(/\s/g, '');
  changeLegalEntity({ rcs: cleanRcs });

  if ( isValidRcs(cleanRcs) ) {
    try {
      const companyDetails = await fetchCompanyDetailsByRcs(cleanRcs);
      if (companyDetails) {
        // forcer la propriété rcs si companyDetails ne l'inclut pas
        const companyWithRcs = { ...companyDetails, rcs: companyDetails.rcs?.toString().replace(/\D/g, '') ?? cleanRcs };
        if (!legalFormOptions.includes(companyWithRcs.legalForm)) legalFormOptions.unshift(companyWithRcs.legalForm);
        changeLegalEntity(companyWithRcs);
      }
    } catch (error) {
      console.log(error);
    }
  }
};


    return(

        <div className = "mb-3 container-fluid">

            <div className = "mb-3 row">

                <RcsInput
                    checked = { checked }
                    errors = { errors }
                    rcs = { legalEntity.rcs }
                    onChange = { handleRcsChange }
                />

                <label htmlFor = "denomination1" className = "col-sm-2 col-form-label required"> Dénomination  <strong className="text-danger">*</strong>  </label>

                <div className = "col-sm-4">
                    <input
                        className = { `description-input form-control ${ ( checked && errors.companyName ) ? 'is-invalid' : ''}` }
                        type = "text"
                        name = "companyName"
                        id = "companyName"
                        value = { legalEntity.companyName }
                        onChange = { event => changeLegalEntity({ companyName: event.target.value }) }
                    />
                    {  ( checked && errors.companyName ) && <div className="invalid-feedback"> { errors.companyName } </div> }
                    <FieldDescription description = "Nom de la société, comme mentionné dans les statuts." />
                </div>

            </div>

            <div className = "mb-3 row">

                <label htmlFor = "sociteChoice" className = "col-sm-2 col-form-label"> Forme juridique <strong className="text-danger">*</strong>  </label>

                <div className = 'col-sm-4'>
                    <select
                        className = { `form-control ${ ( checked && errors.legalForm ) ? 'is-invalid' : ''}` }
                        name = "legalForm"
                        id = "legalForm"
                        onChange = { event => changeLegalEntity({ legalForm: event.target.value }) }
                        value = {legalEntity.legalForm || ""}
                    >
                        {
                            legalFormOptions.map((option, index) => 
                                <option key = { index } value = { option }>
                                    { option }
                                </option>
                            )
                        }
                    </select>
                    {  ( checked && errors.legalForm ) && <div className="invalid-feedback"> { errors.legalForm } </div> }
                </div>

                <CapitalInput
                    inputLabel = "Capital"
                    fieldDescription = "Montant du capital tel que mentionné dans les statuts. Par exemple : 1535,65"
                    fieldKey = "capital"
                    capital = { legalEntity.capital }
                    checked = { checked }
                    errors = { errors }
                    onChange = { changeLegalEntity }
                />

            </div>

            <div className = "mb-3 row">

                <label htmlFor = "address" className = "col-sm-2 col-form-label">Adresse <strong className="text-danger">*</strong>  </label>

                <div className = "col-sm-4">
                    <input
                        className = { `form-control ${ ( checked && errors.address ) ? 'is-invalid' : ''}` }
                        type = "text"
                        name = "address"
                        id = "address"
                        value = { legalEntity.address }
                        onChange = { event => changeLegalEntity({ address: event?.target.value}) }
                    />
                    {  ( checked && errors.address ) && <div className="invalid-feedback"> { errors.address } </div> }
                </div>

                <label htmlFor = "additionalAddress" className = "col-sm-2 col-form-label">Adresse suite </label>

                <div className = "col-sm-4">
                    <input
                        className = "form-control"
                        type = "text"
                        name = "additionalAddress"
                        id = "additionalAddress"
                        value = { legalEntity.additionalAddress }
                        onChange = { event => changeLegalEntity({ additionalAddress: event.target.value }) }
                    />
                </div>

            </div>

            <div className = "mb-3 row">

                <PostalCodeField
                    checked = { checked }
                    onChange = { changeLegalEntity }
                    postalCode = { legalEntity.postalCode }
                    errors = { errors }
                    style={ { width: '100%', gap: '4px' } }
                />

                <label htmlFor = "city" className = "col-sm-2 col-form-label"> Ville <strong className="text-danger">*</strong>  </label>

                <div className = "col-sm-4">
                    <select
                        className = { `form-control ${ ( checked && errors.city ) ? 'is-invalid' : ''}` }
                        name = "city"
                        id = "city"
                        onChange = { event => changeLegalEntity({ city: event.target.value }) }
                        value = { legalEntity.city }
                    >
                        {
                            legalEntity.cities?.map( (city, key) => 
                                <option key = { key } value = { city }>
                                    { city }
                                </option>
                            )
                        }
                    </select>
                    {  ( checked && errors.city ) && <div className="invalid-feedback"> { errors.city } </div> }
                </div>

            </div>

            <div className = "mb-3 row">

                <label htmlFor = "registrationOffice" className = "col-sm-2 col-form-label">Greffe <strong className="text-danger">*</strong>  </label>

                <div className = "col-sm-4">
                    <input
                        className = { `form-control ${ ( checked && errors.registrationOffice ) ? 'is-invalid' : ''}` }
                        type = "text"
                        id = "registrationOffice"
                        value = { legalEntity.registrationOffice !== "" ? getCityWithPreposition( legalEntity.registrationOffice ) : "" }
                        onChange = { event => changeLegalEntity({ registrationOffice: event.target.value }) }
                    />
                    {  ( checked && errors.registrationOffice ) && <div className="invalid-feedback"> { errors.registrationOffice } </div> }
                </div>

            </div>

            <div className = "mb-3 row">
                <div className="col-sm-12 p-2 title">
                    <span className="text-white"> { representativeLabel } </span>
                </div>
            </div>

            <div className = "mb-3 row">
            
                <label htmlFor = "civilite" className = "col-sm-2 col-form-label"> Civilité <strong className="text-danger">*</strong>  </label>

                <div className = "col-sm-4">

                    <select
                        className = { `form-select ${ ( checked && errors.civility ) ? 'is-invalid' : ''}` }
                        name = "civilite"
                        id = "civility"
                        value = { legalEntity.civility }
                        onChange = { event => changeLegalEntity({ civility: event.target.value as "Monsieur" | "Madame" } )}
                    >
                        {
                            civilities.map( option => 
                                <option key = { option } value = {option}>
                                    {option}
                                </option>
                            )
                        }

                    </select>
                    {  ( checked && errors.civility ) && <div className="invalid-feedback"> { errors.civility } </div> }

                </div>

            </div>

            <div className = "mb-3 row">

                <label htmlFor = "nom" className = "col-sm-2 col-form-label">Nom <strong className="text-danger">*</strong> </label>

                <div className = "col-sm-4">

                    <input
                        className = { `form-control ${ ( checked && errors.lastName ) ? 'is-invalid' : ''}` }
                        type = "text"
                        name = "nom"
                        id = "lastName"
                        value = { legalEntity.lastName }
                        onChange = { event => changeLegalEntity({ lastName: event.target.value })}

                    />
                    {  ( checked && errors.lastName ) && <div className="invalid-feedback"> { errors.lastName } </div> }

                </div>
            </div>

            <div className = "mb-3 row">

                <label htmlFor = "prenom" className = "col-sm-2 col-form-label">Prénom <strong className="text-danger">*</strong> </label>

                <div className = "col-sm-4">

                    <input
                        className = { `form-control ${ ( checked && errors.firstName ) ? 'is-invalid' : ''}` }
                        type = "text"
                        name = "firstName"
                        value = { legalEntity.firstName }
                        onChange = { event => changeLegalEntity({ firstName: event.target.value })}
                    />
                    {  ( checked && errors.firstName ) && <div className="invalid-feedback"> { errors.firstName } </div> }

                </div>

            </div>

        </div>
    )
}